/* Tiendazo Viral · Pasarela de algodón. Componentes nativos (sin librerías), uno por sección. */
(() => {
  if (window.TiendazoCotton) return;
  window.TiendazoCotton = true;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  /* Portada: el sello flotante sigue al cursor muy poquito (solo con mouse y sin "menos movimiento"). */
  class TzcHero extends HTMLElement {
    connectedCallback() {
      this.badge = this.querySelector('[data-tzc-depth]');
      if (!this.badge || this.dataset.depth !== 'true') return;
      this.ac = new AbortController();
      const { signal } = this.ac;
      this.addEventListener('pointermove', (e) => {
        if (e.pointerType !== 'mouse' || reducedMotion() || !finePointer()) return;
        const r = this.getBoundingClientRect();
        const x = ((e.clientX - r.left) / r.width - 0.5) * 12;
        const y = ((e.clientY - r.top) / r.height - 0.5) * 12;
        this.badge.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
      }, { signal });
      this.addEventListener('pointerleave', () => { this.badge.style.transform = ''; }, { signal });
    }
    disconnectedCallback() { if (this.ac) this.ac.abort(); }
  }

  /* Pasarela de productos: centro grande + laterales en perspectiva. */
  class TzcRunway extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('.tzc-stage');
      this.slides = Array.from(this.querySelectorAll('.tzc-slide'));
      if (!this.stage || !this.slides.length) return;
      this.dots = Array.from(this.querySelectorAll('[data-tzc-dot]'));
      this.titleEl = this.querySelector('[data-tzc-title]');
      this.subEl = this.querySelector('[data-tzc-sub]');
      this.statusEl = this.querySelector('[data-tzc-status]');
      this.ctaEl = this.querySelector('[data-tzc-cta]');
      this.liveEl = this.querySelector('[data-tzc-live]');
      this.pauseBtn = this.querySelector('[data-tzc-pause]');
      this.interval = Math.max(2500, (parseFloat(this.dataset.interval) || 4.2) * 1000);
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1 && !reducedMotion();
      this.current = 0;
      this.step = 0;
      this.state = { user: false, hover: false, focus: false, editor: false, offscreen: false };
      this.dragEnd = 0;

      // Primer dibujo sin transición: los bodies no "vuelan" a su lugar al cargar.
      this.classList.add('is-ready', 'is-instant');
      this.ac = new AbortController();
      this.bind(this.ac.signal);
      this.render(false);
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.remove('is-instant')));
      if (this.pauseBtn && this.autoplay) this.pauseBtn.hidden = false;
      this.sync();
    }

    disconnectedCallback() {
      if (this.ac) this.ac.abort();
      clearTimeout(this.timer);
      if (this.io) this.io.disconnect();
    }

    bind(signal) {
      const on = (el, type, fn) => el.addEventListener(type, fn, { signal });
      const prev = this.querySelector('[data-tzc-prev]');
      const next = this.querySelector('[data-tzc-next]');
      if (prev) on(prev, 'click', () => { this.interact(); this.go(this.current - 1, true); });
      if (next) on(next, 'click', () => { this.interact(); this.go(this.current + 1, true); });
      this.dots.forEach((dot) => on(dot, 'click', () => {
        this.interact();
        this.go(parseInt(dot.dataset.tzcDot, 10), true);
      }));

      // Primer clic en un lateral: lo centra. Clic en el central: abre su ficha.
      this.slides.forEach((slide, i) => {
        on(slide, 'click', (e) => {
          if (performance.now() - this.dragEnd < 450) { e.preventDefault(); return; }
          if (i !== this.current) {
            e.preventDefault();
            this.interact();
            this.go(i, true);
          }
        });
      });

      // Teclado: flechas, Inicio y Fin cuando el foco está en la pasarela (sin pisar Alt+Flecha del navegador).
      on(this.stage, 'keydown', (e) => {
        if (e.altKey || e.ctrlKey || e.metaKey) return;
        const keys = { ArrowLeft: -1, ArrowRight: 1 };
        let target = null;
        if (e.key in keys) target = this.current + keys[e.key];
        else if (e.key === 'Home') target = 0;
        else if (e.key === 'End') target = this.slides.length - 1;
        if (target === null) return;
        e.preventDefault();
        const slideHadFocus = this.slides.includes(document.activeElement);
        this.interact();
        // Si el foco pasa a la tarjeta, el lector ya la anuncia: no repetir con el aviso.
        this.go(target, !slideHadFocus);
      });

      // Deslizar con el dedo o el mouse (sin bloquear el scroll vertical ni el zoom).
      let start = null;
      on(this.stage, 'pointerdown', (e) => {
        if (!e.isPrimary || e.button !== 0) return;
        start = { x: e.clientX, y: e.clientY };
      });
      on(this.stage, 'pointerup', (e) => {
        if (!start) return;
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        start = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) {
          this.dragEnd = performance.now();
          this.interact();
          this.go(this.current + (dx < 0 ? 1 : -1), true);
        }
      });
      on(this.stage, 'pointercancel', () => { start = null; });
      on(this.stage, 'dragstart', (e) => e.preventDefault());

      // Pausas: cursor encima, foco de teclado, botón de pausa, fuera de pantalla, pestaña oculta.
      on(this, 'pointerenter', (e) => { if (e.pointerType === 'mouse') { this.state.hover = true; this.sync(); } });
      on(this, 'pointerleave', (e) => { if (e.pointerType === 'mouse') { this.state.hover = false; this.sync(); } });
      on(this, 'focusin', (e) => {
        if (!e.target.matches(':focus-visible')) return;
        this.state.focus = true;
        this.sync();
      });
      on(this, 'focusout', (e) => {
        if (!this.contains(e.relatedTarget)) { this.state.focus = false; this.sync(); }
      });
      if (this.pauseBtn) {
        on(this.pauseBtn, 'click', () => {
          this.state.user = !this.state.user;
          // "Reanudar" manda: aunque el foco quede en el botón, la pasarela vuelve a avanzar.
          if (!this.state.user) this.state.focus = false;
          this.pauseBtn.setAttribute('aria-pressed', String(this.state.user));
          this.sync();
        });
      }
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => { this.state.offscreen = !entry.isIntersecting; });
          this.sync();
        }, { threshold: 0.2 });
        this.io.observe(this);
      }
      on(document, 'visibilitychange', () => this.sync());

      // Editor del tema: al elegir un body en el panel, la pasarela lo muestra y se queda quieta.
      if (window.Shopify && window.Shopify.designMode) {
        on(document, 'shopify:block:select', (e) => {
          const slide = e.target.closest && e.target.closest('.tzc-slide');
          const i = this.slides.indexOf(slide);
          if (i < 0) return;
          this.state.editor = true;
          this.go(i, false);
        });
        on(document, 'shopify:block:deselect', (e) => {
          if (this.slides.includes(e.target.closest && e.target.closest('.tzc-slide'))) {
            this.state.editor = false;
            this.sync();
          }
        });
      }
    }

    // Quien toca un control toma el mando: la pasarela deja de avanzar sola.
    interact() {
      if (!this.autoplay || this.state.user) return;
      this.state.user = true;
      if (this.pauseBtn) this.pauseBtn.setAttribute('aria-pressed', 'true');
      this.sync();
    }

    distance(i) {
      const n = this.slides.length;
      let d = i - this.current;
      if (d > n / 2) d -= n;
      if (d < -n / 2) d += n;
      return d;
    }

    go(i, announce) {
      const n = this.slides.length;
      const next = ((i % n) + n) % n;
      this.step = this.distance(next);
      this.current = next;
      this.render(announce);
      this.sync();
    }

    render(announce) {
      this.slides.forEach((slide, i) => {
        const d = this.distance(i);
        const active = d === 0;
        const prevPos = slide.dataset.pos;
        const pos = Math.abs(d) > 2 ? 'hidden' : String(d);
        // Una tarjeta que "da la vuelta" (de un extremo al otro) salta sin animar, para que no cruce por detrás de la central.
        const wrapped = this.step && prevPos && prevPos !== 'hidden' && pos !== 'hidden' && Number(pos) !== Number(prevPos) - this.step;
        if (wrapped) {
          slide.style.transition = 'none';
          slide.dataset.pos = pos;
          void slide.offsetWidth;
          slide.style.transition = '';
        } else {
          slide.dataset.pos = pos;
        }
        slide.tabIndex = active ? 0 : -1;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.classList.toggle('is-active', active);
      });
      // Si el foco quedó en una tarjeta que ya no es la central (por ejemplo después de arrastrar), pasa a la central.
      const focused = document.activeElement;
      if (focused !== this.slides[this.current] && this.slides.includes(focused)) {
        this.slides[this.current].focus({ preventScroll: true });
      }
      this.dots.forEach((dot, i) => {
        const on = i === this.current;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-current', on ? 'true' : 'false');
      });

      const s = this.slides[this.current];
      const available = s.dataset.available === 'true';
      if (this.titleEl) { this.titleEl.textContent = s.dataset.title; this.titleEl.href = s.href; }
      if (this.subEl) this.subEl.textContent = s.dataset.price;
      if (this.statusEl) this.statusEl.hidden = available;
      if (this.ctaEl && this.ctaEl.firstChild) {
        this.ctaEl.href = s.href;
        this.ctaEl.firstChild.nodeValue = available ? this.dataset.cta : this.dataset.ctaSoldOut;
      }
      if (announce && this.liveEl) {
        const soldOut = available ? '' : `, ${this.dataset.soldOut}`;
        this.liveEl.textContent = `${s.dataset.title}, ${s.dataset.price}${soldOut}`;
      }
    }

    sync() {
      clearTimeout(this.timer);
      const st = this.state;
      const running = this.autoplay && !st.user && !st.hover && !st.focus && !st.editor && !st.offscreen && !document.hidden;
      if (running) this.timer = setTimeout(() => this.go(this.current + 1, false), this.interval);
    }
  }

  /* Explorador de tela: la lupa sigue al cursor o al dedo dentro de la tela. */
  class TzcFabric extends HTMLElement {
    connectedCallback() {
      if (this.dataset.lens !== 'true') return;
      this.ac = new AbortController();
      const move = (e) => {
        const r = this.getBoundingClientRect();
        const x = Math.min(Math.max(e.clientX - r.left, 0), r.width);
        const y = Math.min(Math.max(e.clientY - r.top, 0), r.height);
        this.style.setProperty('--x', `${x}px`);
        this.style.setProperty('--y', `${y}px`);
        this.style.setProperty('--px', `${((x / r.width) * 100).toFixed(1)}%`);
        this.style.setProperty('--py', `${((y / r.height) * 100).toFixed(1)}%`);
      };
      this.addEventListener('pointermove', move, { signal: this.ac.signal });
      this.addEventListener('pointerdown', move, { signal: this.ac.signal });
    }
    disconnectedCallback() { if (this.ac) this.ac.abort(); }
  }

  /* Franja emocional: botón de pausa y se detiene fuera de pantalla. */
  class TzcMarquee extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      const btn = this.querySelector('[data-tzc-marquee-toggle]');
      if (btn) {
        btn.addEventListener('click', () => {
          const paused = this.classList.toggle('is-paused');
          btn.setAttribute('aria-pressed', String(paused));
        }, { signal: this.ac.signal });
      }
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => this.classList.toggle('is-offscreen', !entry.isIntersecting));
        });
        this.io.observe(this);
      }
    }
    disconnectedCallback() {
      if (this.ac) this.ac.abort();
      if (this.io) this.io.disconnect();
    }
  }

  define('tzc-hero', TzcHero);
  define('tzc-runway', TzcRunway);
  define('tzc-fabric', TzcFabric);
  define('tzc-marquee', TzcMarquee);
})();
